import { useQuery, MutationTuple } from "@apollo/client";
import { Typography, Button } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrayParam,
  JsonParam,
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import { isUUID } from "@stw/uuid";

import {
  ArchiveCampaignMutation,
  ArchiveCampaignMutationVariables,
  ArchiveCampaignsMutation,
  ArchiveCampaignsMutationVariables,
  CampaignFilterInput,
  CampaignStatus,
  CopyCampaignMutation,
  CopyCampaignMutationVariables,
  CreateCampaignMutation,
  CreateCampaignMutationVariables,
  GetCampaignStatsForListQuery,
  GetCampaignStatsForListQueryVariables,
  IntegrationServiceType,
  ListCampaignsQuery,
  ListCampaignsQueryVariables,
  UnarchiveCampaignMutation,
  UnarchiveCampaignMutationVariables,
  CampaignMetadataFilterInput,
  GetSubaccountMetadataFieldsQuery,
  GetSubaccountMetadataFieldsQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { DeleteManyState, TableBox } from "~frontend/components/Table";
import {
  AdvancedFilterDialogue,
  replaceSpaceWithUnderscore,
} from "~frontend/components/campaignMetadata/AdvancedFilterDialogue";
import { NON_ARCHIVED_STATUS_FILTERS } from "~frontend/components/threads/constants";
import {
  LIST_CAMPAIGNS,
  CREATE_CAMPAIGN,
  ARCHIVE_CAMPAIGN,
  UNARCHIVE_CAMPAIGN,
  COPY_CAMPAIGN,
  GET_CAMPAIGN_STATS_AND_METADATA_FIELDS,
  ARCHIVE_CAMPAIGNS,
} from "~frontend/graphql/campaigns";
import { GET_SUBACCOUNT_METADATA_FIELDS } from "~frontend/graphql/subaccounts";
import { useIntegrations } from "~frontend/hooks/useIntegrations";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import {
  useSubaccount,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import { plural } from "~frontend/util/plural";
import useMutation from "~frontend/util/useMutation";

import CampaignListControls from "./CampaignListControls";
import CampaignTable from "./CampaignTable";
import { CAMPAIGN_SORTS, CAMPAIGN_STATUSES } from "./constants";

export function CampaignsList(): ReactElement {
  const { subaccountLink } = useSubaccount()!;
  const subaccount = useSubaccountAdminConfig()!;
  useBreadcrumbs([
    {
      label: "Campaigns",
      path: subaccountLink("/campaigns"),
    },
  ]);

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 50)
  );
  const [statusFilter, setStatusFilter] = useQueryParam(
    "status",
    withDefault(ArrayParam, NON_ARCHIVED_STATUS_FILTERS)
  );
  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, CAMPAIGN_SORTS[0].value)
  );

  const [metadataFilters, setMetadataFilters] = useQueryParam<
    CampaignMetadataFilterInput[] | undefined
  >("metadata", withDefault(JsonParam, []));
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);

  const filter: CampaignFilterInput = {};
  if (search) {
    if (isUUID(search)) {
      filter.idIn = [search.trim().toLowerCase()];
    } else {
      filter.nameContains = search;
    }
  }
  if (statusFilter && statusFilter.length > 0) {
    filter.statusIn = statusFilter.filter(status =>
      CAMPAIGN_STATUSES.find(s => s.value === status)
    ) as CampaignStatus[];
  }

  const sortValue =
    CAMPAIGN_SORTS.find(availableSort => availableSort.value === sort)?.value ||
    CAMPAIGN_SORTS[0].value;

  filter.metadata = metadataFilters?.map(af => {
    return {
      ...af,
      name: (af.name as string).replaceAll("_", " "),
    };
  });

  const listCampaignsResult = useQuery<
    ListCampaignsQuery,
    ListCampaignsQueryVariables
  >(LIST_CAMPAIGNS, {
    variables: {
      input: {
        limit: pageSize,
        offset: page * pageSize,
        filter,
        sort: sortValue,
        subaccountId: subaccount?.id,
      },
    },
    skip: !subaccount?.id,
    fetchPolicy: "network-only",
  });

  const campaigns = listCampaignsResult.data?.listCampaigns.campaigns;

  const getCampaignStatsAndMetadata = useQuery<
    GetCampaignStatsForListQuery,
    GetCampaignStatsForListQueryVariables
  >(GET_CAMPAIGN_STATS_AND_METADATA_FIELDS, {
    variables: {
      input: {
        limit: pageSize,
        offset: page * pageSize,
        filter,
        sort: sortValue,
        subaccountId: subaccount?.id,
      },
    },
    skip: !subaccount?.id,
    fetchPolicy: "network-only",
  });

  const {
    loading: metadataLoading,
    data: metadataData,
    error: metadataError,
  } = useQuery<
    GetSubaccountMetadataFieldsQuery,
    GetSubaccountMetadataFieldsQueryVariables
  >(GET_SUBACCOUNT_METADATA_FIELDS, {
    variables: {
      input: {
        subaccountId: subaccount?.id,
      },
    },
  });

  const {
    loading: listIntegrationsLoading,
    error: listIntegrationsError,
    hasIntegration: hasActBlueIntegration,
  } = useIntegrations({ service: IntegrationServiceType.ActBlueWebhook });

  const [createCampaign, { loading: createCampaignLoading }] = useMutation<
    CreateCampaignMutation,
    CreateCampaignMutationVariables
  >(CREATE_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not create campaign",
    refetchQueries: ["listCampaigns"],
  });

  const [copyCampaign] = useMutation<
    CopyCampaignMutation,
    CopyCampaignMutationVariables
  >(COPY_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not copy campaign",
    refetchQueries: ["listCampaigns"],
  });

  const [archiveCampaign] = useMutation<
    ArchiveCampaignMutation,
    ArchiveCampaignMutationVariables
  >(ARCHIVE_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not archive campaign",
    refetchQueries: ["listCampaigns"],
    awaitRefetchQueries: true,
  });

  const [unarchiveCampaign] = useMutation<
    UnarchiveCampaignMutation,
    UnarchiveCampaignMutationVariables
  >(UNARCHIVE_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not unarchive campaign",
    refetchQueries: ["listCampaigns"],
    awaitRefetchQueries: true,
  });

  const [archiveCampaigns] = useMutation<
    ArchiveCampaignsMutation,
    ArchiveCampaignsMutationVariables
  >(ARCHIVE_CAMPAIGNS, {
    refetchQueries: ["listCampaigns"],
    awaitRefetchQueries: true,
  });

  const [deleteManyState, setDeleteManyState] = useState<DeleteManyState>({
    selecting: false,
  });

  const resetDeleteManyState = useCallback(
    () => setDeleteManyState({ selecting: false }),
    [setDeleteManyState]
  );

  // clear delete many selections when tags update
  useEffect(resetDeleteManyState, [campaigns, resetDeleteManyState]);

  const snackbar = useSnackbar();
  const confirm = useConfirm();

  const handleArchiveSelectedCampaigns = useCallback(async () => {
    if (!deleteManyState.selecting) {
      return;
    }
    const selectedCampaigns = campaigns?.filter(campaign =>
      deleteManyState.selectedIds.includes(campaign.id)
    );
    if (!selectedCampaigns?.length) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Archive Campaigns",
      content: `Are you sure you want to archive the following campaigns: ${selectedCampaigns
        .map(campaign => `"${campaign.name}"`)
        .join(", ")}?`,
    });
    if (!confirmed) {
      return;
    }

    const { errors, data } = await archiveCampaigns({
      variables: {
        input: { campaignIds: selectedCampaigns.map(tag => tag.id) },
      },
    });

    if (errors) {
      snackbar.enqueueSnackbar(
        `Error deleting campaigns: ${errors[0].message}`,
        {
          variant: "error",
        }
      );
      throw errors;
    } else if (data?.archiveCampaigns.failedIds.length) {
      resetDeleteManyState();
      snackbar.enqueueSnackbar(
        `${plural(data.archiveCampaigns.successIds.length, "campaign", "campaigns")} archived, but ${plural(data.archiveCampaigns.failedIds.length, "campaign", "campaigns")} could not be archived (may have been running or already archived).`,
        { variant: "warning" }
      );
    } else if (data?.archiveCampaigns.successIds.length) {
      resetDeleteManyState();
      snackbar.enqueueSnackbar(
        `${plural(data.archiveCampaigns.successIds.length, "campaign", "campaigns")} archived successfully.`,
        { variant: "success" }
      );
    }
  }, [
    deleteManyState,
    resetDeleteManyState,
    campaigns,
    confirm,
    archiveCampaigns,
    snackbar,
  ]);

  const navigate = useNavigate();

  const goToCampaign = (shortId: number) =>
    navigate(subaccountLink(`/campaigns/${shortId}/edit/basics`));

  async function handleNewCampaign() {
    const { data, errors } = await createCampaign({
      variables: {
        input: {
          name: "New Campaign",
          subaccountId: subaccount.id,
        },
      },
    });

    if (!errors) {
      goToCampaign(data!.createCampaign.shortId)?.catch(console.error);
    }
  }

  async function handleCopyCampaign(campaignId: string) {
    const { data, errors } = await copyCampaign({
      variables: {
        input: {
          campaignId,
        },
      },
    });

    if (!errors) {
      goToCampaign(data!.copyCampaign.shortId)?.catch(console.error);
    }
  }

  function makeCampaignStatusMutationCallback(
    mutation: MutationTuple<any, { input: { campaignId: string } }>[0]
  ) {
    return async (campaignId: string) => {
      const { errors } = await mutation({
        variables: { input: { campaignId } },
      });

      if (errors) {
        // The status mutation failed -- probably someone else changed the status
        // of the campaign out from under us. Re-fetch the campaign list to make
        // sure we have up-to-date data
        await listCampaignsResult.refetch();
      }
    };
  }

  function resetSearchState() {
    setSearch(undefined);
    setPage(undefined);
    setStatusFilter(undefined);
    setMetadataFilters(undefined);
  }

  const metadataFields = metadataData?.getSubaccount.campaignMetadataFields
    .filter(f => !f.archived)
    .map(field => ({
      ...field,
      name: replaceSpaceWithUnderscore(field.name),
      label: field.name,
    }));

  const totalResults = listCampaignsResult.data?.listCampaigns?.totalCount || 0;
  const searchResultHasData = campaigns && campaigns.length > 0;
  const showAdvancedFilterButton =
    (metadataFields && metadataFields.length > 0) || false;
  const filtersApplied =
    search.length > 0 ||
    statusFilter !== NON_ARCHIVED_STATUS_FILTERS ||
    (metadataFilters && metadataFilters.length > 0);

  return (
    <PageContainer>
      <PageHeader>Campaigns</PageHeader>
      <AsyncWrapper
        loading={[
          !subaccount?.id,
          listCampaignsResult.loading,
          listIntegrationsLoading,
          metadataLoading,
        ]}
        errors={[
          listCampaignsResult.error,
          listIntegrationsError,
          metadataError,
        ]}
      >
        {metadataFields && metadataFields.length > 0 && (
          <AdvancedFilterDialogue
            open={showAdvancedFilter}
            close={() => setShowAdvancedFilter(false)}
            setAdvancedFilters={setMetadataFilters}
            metadataFields={metadataFields}
            metadataFilters={metadataFilters || []}
          />
        )}
        <CampaignListControls
          onAdvancedSearchClick={() => setShowAdvancedFilter(true)}
          advancedSearchFiltersApplied={metadataFilters?.length || 0}
          showAdvancedSearchButton={showAdvancedFilterButton}
          onClickNewCampaign={handleNewCampaign}
          createCampaignLoading={createCampaignLoading}
          statusFilter={statusFilter as string[]}
          setStatusFilter={newFilter => {
            setStatusFilter(newFilter);
            setPage(0);
          }}
          search={search}
          setSearch={newSearch => {
            setSearch(newSearch);
            setPage(0);
          }}
          sort={sort}
          setSort={setSort}
          noMessagingStrategies={
            !subaccount.messagingStrategies?.find(
              s => s.isConfigured && s.isEnabled
            )
          }
          deleteManyState={deleteManyState}
          setDeleteManyState={setDeleteManyState}
          onDeleteSelected={handleArchiveSelectedCampaigns}
        />
        <FlexBox
          direction="row"
          sx={{ px: 5, pb: 2 }}
          justifyContent="flex-start"
          alignItems="center"
        >
          {searchResultHasData && (
            <Typography variant="body2Bold">
              {plural(totalResults, "Result", "Results")}
            </Typography>
          )}
          {filtersApplied && (
            <Button variant="link" onClick={resetSearchState}>
              <Typography variant="body2">Clear Filters</Typography>
            </Button>
          )}
        </FlexBox>
        <TableBox sx={{ px: 5, py: 2 }}>
          {searchResultHasData ? (
            <CampaignTable
              showFundraisingFields={hasActBlueIntegration}
              showClicks={subaccount.linkShortenerDomains?.length > 0}
              rows={campaigns}
              campaignStats={
                getCampaignStatsAndMetadata.data?.listCampaigns.campaigns
              }
              handleChangePage={setPage}
              handleChangeRowsPerPage={newRowsPerPage => {
                setPageSize(newRowsPerPage);
                setPage(0);
              }}
              rowsPerPage={pageSize}
              page={page}
              totalCount={listCampaignsResult.data!.listCampaigns.totalCount}
              handleArchive={makeCampaignStatusMutationCallback(
                archiveCampaign
              )}
              handleUnarchive={makeCampaignStatusMutationCallback(
                unarchiveCampaign
              )}
              handleCopy={handleCopyCampaign}
              deleteManyState={deleteManyState}
              onChangeDeleteManyState={setDeleteManyState}
            />
          ) : (
            <FlexBox
              alignItems="center"
              justifyContent="center"
              sx={{ py: 5, px: 3 }}
            >
              <Typography>
                No campaigns created. Your campaigns will appear here once
                created using the “new campaign” button above.
              </Typography>
            </FlexBox>
          )}
        </TableBox>
      </AsyncWrapper>
    </PageContainer>
  );
}
